/**
 * Contact form functionality
 */
class ContactForm {
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('.form-submit');
    this.originalButtonText = this.submitButton.textContent;
    
    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    
    // Form validation
    const inputs = this.form.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('blur', this.validateField.bind(this));
      input.addEventListener('input', this.clearFieldError.bind(this));
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }

    this.setLoadingState(true);

    try {
      const formData = new FormData(this.form);
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        }
      });

      if (response.ok) {
        this.showSuccess('Thank you! Your message has been sent successfully.');
        this.form.reset();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      this.showError('Sorry, there was an error sending your message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  validateForm() {
    const inputs = this.form.querySelectorAll('input[required], textarea[required], select[required]');
    let isValid = true;

    inputs.forEach(input => {
      if (!this.validateField({ target: input })) {
        isValid = false;
      }
    });

    return isValid;
  }

  validateField(event) {
    const field = event.target;
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    // Remove existing error
    this.clearFieldError(event);

    // Required field validation
    if (field.hasAttribute('required') && !value) {
      errorMessage = 'This field is required';
      isValid = false;
    }
    // Email validation
    else if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errorMessage = 'Please enter a valid email address';
        isValid = false;
      }
    }
    // Phone validation
    else if (field.type === 'tel' && value) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]+$/;
      if (!phoneRegex.test(value)) {
        errorMessage = 'Please enter a valid phone number';
        isValid = false;
      }
    }

    if (!isValid) {
      this.showFieldError(field, errorMessage);
    }

    return isValid;
  }

  showFieldError(field, message) {
    field.classList.add('error');
    
    // Remove existing error message
    const existingError = field.parentNode.querySelector('.field-error');
    if (existingError) {
      existingError.remove();
    }

    // Add error message
    const errorElement = document.createElement('div');
    errorElement.className = 'field-error';
    errorElement.textContent = message;
    field.parentNode.appendChild(errorElement);
  }

  clearFieldError(event) {
    const field = event.target;
    field.classList.remove('error');
    
    const errorElement = field.parentNode.querySelector('.field-error');
    if (errorElement) {
      errorElement.remove();
    }
  }

  showSuccess(message) {
    this.showMessage(message, 'success');
  }

  showError(message) {
    this.showMessage(message, 'error');
  }

  showMessage(message, type) {
    // Remove existing messages
    const existingMessage = this.form.querySelector('.form-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create message element
    const messageElement = document.createElement('div');
    messageElement.className = `form-message form-${type}`;
    messageElement.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        ${type === 'success' 
          ? '<path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>'
          : '<path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>'
        }
      </svg>
      <span>${message}</span>
    `;

    // Insert at the top of the form
    this.form.insertBefore(messageElement, this.form.firstChild);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.remove();
      }
    }, 5000);

    // Scroll to message
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  setLoadingState(loading) {
    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = `
        <div class="loading-spinner"></div>
        Sending...
      `;
    } else {
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.originalButtonText;
    }
  }
}

/**
 * Initialize contact forms when DOM is loaded
 */
document.addEventListener('DOMContentLoaded', function() {
  const contactForms = document.querySelectorAll('.contact-form');
  contactForms.forEach(form => {
    new ContactForm(form);
  });

  // Phone number formatting
  const phoneInputs = document.querySelectorAll('input[type="tel"]');
  phoneInputs.forEach(input => {
    input.addEventListener('input', function(e) {
      // Remove all non-numeric characters except + and spaces
      let value = e.target.value.replace(/[^\d\+\s\-\(\)]/g, '');
      e.target.value = value;
    });
  });

  // Auto-resize textareas
  const textareas = document.querySelectorAll('textarea');
  textareas.forEach(textarea => {
    textarea.addEventListener('input', function() {
      this.style.height = 'auto';
      this.style.height = this.scrollHeight + 'px';
    });
  });
});

// Add CSS for form validation styles
const styles = `
  .form-input.error,
  .form-textarea.error,
  .form-select.error {
    border-color: var(--color-error, #ef4444);
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  .field-error {
    color: var(--color-error, #ef4444);
    font-size: 0.85rem;
    margin-top: var(--spacing-xs, 0.25rem);
    display: flex;
    align-items: center;
    gap: var(--spacing-xs, 0.25rem);
  }

  .form-message {
    padding: var(--spacing-md, 1rem);
    border-radius: var(--border-radius-md, 0.5rem);
    margin-bottom: var(--spacing-lg, 1.5rem);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm, 0.5rem);
    animation: slideIn 0.3s ease-out;
  }

  .form-success {
    background: rgba(46, 125, 50, 0.1);
    color: #2e7d32;
    border: 1px solid #2e7d32;
  }

  .form-error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid #ef4444;
  }

  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);